<template>
	<div>
		<div style="min-height:2.25rem;" class="d-flex align-start">
			<slot name="title">
				<component :is="agent ? 'span' : 'strong'">{{ title }}</component>
			</slot>

			<v-btn
				v-if="allowEdit"
				dark small fab depressed color="q_green_1"
				class="edit-button ml-auto"
				@click="showDialog"
			>
				<v-icon>icon-edit-q</v-icon>
			</v-btn>
		</div>

		<div class="ml-2">
			<v-checkbox
				v-for="option in predefinedOptions" :key="option.label"
				v-model="option.value"
				:label="option.label"
				color="q_green_1"
				readonly
				:ripple="false"
				hide-details
				class="mt-1"
			/>
			<v-checkbox
				v-model="otherOption.value"
				:label="otherOption.content ? `${otherOption.label} (${otherOption.content})` : otherOption.label"
				color="q_green_1"
				readonly
				:ripple="false"
				hide-details
				class="mt-1"
			/>
		</div>

		<v-dialog
			v-if="doShowDialog"
			v-model="doShowDialog"
			persistent
			width="95%"
			max-width="800px"
		>
			<v-card>
				<v-card-title>
					Enter your Lead Language Preferences
				</v-card-title>
				<v-divider />

				<div style="height:4px;">
					<v-progress-linear v-if="loading" color="q_green_1" indeterminate />
				</div>

				<v-card-text>
					<strong><p class="mt-2 mb-0" v-html="title" /></strong>
					<v-checkbox
						v-for="option in dialogPredefinedOptions" :key="option.label"
						v-model="option.value"
						:label="option.label"
						color="q_green_1"
						hide-details
						class="mt-2"
					/>
					<v-checkbox
						v-model="dialogOtherOption.value"
						:label="dialogOtherOption.value ? `${dialogOtherOption.label}:` : dialogOtherOption.label"
						color="q_green_1"
						hide-details
						class="mt-2"
					/>
					<div style="min-height:4rem;" class="mt-2 ml-sm-8 d-flex align-start">
						<v-text-field
							v-if="dialogOtherOption.value"
							ref="inputField"
							v-model="dialogOtherOption.content"
							:placeholder="inputPlaceholder"
							:rules="[
								(value) => !!value || requiredMessage,
								(value) => value.length <= 255 || invalidLengthMessage,
								(value) => inputPattern.test(value) || invalidInputMessage
							]"
							color="q_green_1"
							@change="dialogOtherOption.content = $event?.trim() || ''"
							class="input-field"
						/>
					</div>
				</v-card-text>

				<v-card-actions class="d-flex justify-end pb-4 px-6">
					<v-btn
						depressed
						:disabled="!allowCancel"
						@click="doShowDialog = false"
					>
						Cancel
					</v-btn>
					<v-btn
						dark depressed color="q_green_1"
						:disabled="!allowSave"
						class="save-button ml-4"
						@click="handleSave"
					>
						Save
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import QuilityAPI from '@/store/API/QuilityAPI.js'
import User from '@/store/Models/User'

const inputPattern = /^[a-zA-Z\s-,]+$/

const defaultPredefinedOptions = [
	{ label: 'English', content: 'English-speaking' },
	{ label: 'Spanish', content: 'Spanish-speaking' },
	{ label: 'French', content: 'French-speaking' },
	{ label: 'Mandarin', content: 'Mandarin-speaking' },
	{ label: 'Hindi', content: 'Hindi-speaking' },
]

const defaultOtherOption = { label: 'Other', content: '' }

export default {
	props: {
		agent: { type: Object },
	},
	data () {
		return {
			inputPattern,
			doShowDialog: false,
			loading: false,
			title: 'Interested in leads for consumers that speak:',
			inputPlaceholder: 'Enter your preferred languages',
			invalidLengthMessage: 'Max 255 characters',
			requiredMessage: 'Field is required',
			invalidInputMessage: 'Allowed: letters (A–Z, a–z), spaces, hyphens (-), commas (,)',
			dialogPredefinedOptions: null,
			dialogOtherOption: null,
		}
	},
	computed: {
		allowEdit () {
			if (!this.agent) { return true }
			if (this.hasRole(['SuperAdmin', 'Staff'])) { return true }
			return false
		},
		agentCode () {
			return this.agent ? this.agent.AgentCode : this.user.Agent.AgentCode
		},
		predefinedOptions () {
			const leadLanguagePreferencesValue = (this.agent
				? this.agent.LeadLanguagePreferences
				: this.user.config.LeadLanguagePreferences?.ConfigValue) || ''
			return defaultPredefinedOptions.map((option) => ({
				...option,
				value: leadLanguagePreferencesValue.includes(option.content),
			}))
		},
		otherOption () {
			const leadLanguageOtherValue = (this.agent
				? this.agent.LeadLanguageOther
				: this.user.config.LeadLanguageOther?.ConfigValue) || ''
			const value = !!leadLanguageOtherValue
			const content = leadLanguageOtherValue
			return { ...defaultOtherOption, value, content, }
		},
		hasPredefinedChanges () {
			if (!this.doShowDialog) { return false }
			return this.predefinedOptions.some((option) => (
				this.dialogPredefinedOptions?.some(({ label, value }) => option.label === label && option.value !== value)
			))
		},
		hasOtherChanges () {
			if (!this.doShowDialog) { return false }
			return this.otherOption.content !== this.dialogOtherOption?.content
		},
		hasSelections () {
			if (!this.doShowDialog) { return false }
			const hasPredefinedSelection = this.dialogPredefinedOptions?.some(({ value }) => value) || false
			const hasOtherSelection = !!this.dialogOtherOption?.content || false
			return hasPredefinedSelection || hasOtherSelection
		},
		allowSave () {
			if (!this.doShowDialog) { return false }
			const isAgentProfileOrHasSelections = this.agent || this.hasSelections
			return isAgentProfileOrHasSelections &&
				(this.hasPredefinedChanges || this.hasOtherChanges) &&
				!this.loading
		},
		allowCancel () {
			if (this.agent) { return true }
			const hasPredefinedSelection = this.predefinedOptions.some(({ value }) => value)
			const hasOtherSelection = !!this.otherOption.content
			return hasPredefinedSelection || hasOtherSelection
		},
	},
	watch: {
		doShowDialog (val) {
			if (!val) {
				this.dialogPredefinedOptions = null
				this.dialogOtherOption = null
			}
		},
		dialogOtherOption: {
			deep: true,
			handler () {
				if (!this.dialogOtherOption) { return }
				if (!this.dialogOtherOption.value) {
					this.dialogOtherOption.content = ''
				}
			},
		},
	},
	methods: {
		showDialog () {
			if (!this.allowEdit) { return }
			this.dialogPredefinedOptions = this.predefinedOptions.map((option) => ({ ...option }))
			this.dialogOtherOption = { ...this.otherOption }
			this.doShowDialog = true
		},
		async handleSave () {
			if (this.$refs.inputField && !this.$refs.inputField.valid) {
				this.$refs.inputField?.validate?.(true)
				this.$refs.inputField?.focus?.()
				return
			}
			this.loading = true
			try {
				const predefinedValue = this.dialogPredefinedOptions
					.filter(({ value }) => value)
					.map(({ content }) => content)
					.join(',')
				const otherValue = this.dialogOtherOption.content
				const preferences = [
					{ configName: 'LeadLanguagePreferences', value: predefinedValue, hasChanged: this.hasPredefinedChanges },
					{ configName: 'LeadLanguageOther', value: otherValue, hasChanged: this.hasOtherChanges },
				]
					.filter(({ hasChanged }) => hasChanged)
					.reduce((acc, { configName, value }) => {
						acc[configName] = value
						return acc
					}, {})
				const res = await QuilityAPI.updateLanguagePreference(preferences, this.agentCode)
				if (res.error) {
					this.showError('There was a problem saving Lead Language preferences.<br>', res.message || res.errors)
				} else {
					if (this.agent) {
						this.$emit('refresh')
					} else {
						this.updateUserStore(preferences)
					}
					this.showSuccess('Lead Language preferences have been saved.')
					this.doShowDialog = false
				}
			} catch (err) {
				this.showError('Could not save changes.<br>', err.message || err.msg)
			} finally {
				this.loading = false
			}
		},
		updateUserStore (data = {}) {
			const user = { ...this.user }
			Object.entries(data).forEach(([configName, value]) => {
				user.config[configName] = user.config[configName] ? { ...user.config[configName] } : {}
				user.config[configName].ConfigValue = value
			})
			User.create({ data: user })
		},
	},
}
</script>

<style lang="scss" scoped>
.input-field {
	margin-top: 0;
	padding-top: 0;
	flex: 1 1 auto;
}

.edit-button {
	border-radius: 50%;
}

.save-button {
	&.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
		color: #00000042 !important;
		background-color: #0000001f !important;
	}
}

.v-input.v-input--is-readonly {
	::v-deep .v-input--selection-controls__input + .v-label {
		cursor: unset;
	}
	::v-deep .v-input--selection-controls__input input[role=checkbox] {
		cursor: unset;
	}
}
</style>